/**
 * Sanitizes malformed HTML content from Google Sheets copy-paste artifacts
 * Removes <google-sheets-html-origin> tags and duplicate table markup
 * This is a temporary fix until CMS implements proper content validation
 * 
 * @param htmlContent - Raw HTML content that may contain Google Sheets artifacts
 * @returns Cleaned HTML content
 */
export function sanitizeGoogleSheetsArtifacts(htmlContent: string | null | undefined): string | null | undefined {
  if (!htmlContent || typeof htmlContent !== 'string') {
    return htmlContent;
  }

  // Remove all <google-sheets-html-origin> tags and their contents
  // This handles cases where Google Sheets markup is accidentally included
  let cleanedContent = htmlContent.replace(
    /<google-sheets-html-origin>[\s\S]*?<\/google-sheets-html-origin>/gi,
    ''
  );

  // Remove any remaining standalone <google-sheets-html-origin> tags
  cleanedContent = cleanedContent.replace(
    /<\/?google-sheets-html-origin[^>]*>/gi,
    ''
  );

  // Remove empty style tags that Google Sheets often adds
  cleanedContent = cleanedContent.replace(
    /<style type="text\/css"><\/style>/gi,
    ''
  );

  // Clean up multiple consecutive newlines that may result from removal
  cleanedContent = cleanedContent.replace(/\n{3,}/g, '\n\n');

  // Trim whitespace from start and end
  cleanedContent = cleanedContent.trim();

  return cleanedContent;
}

/**
 * Applies custom attributes to HTML content for proper rendering
 * Sets font sizes for paragraphs and opens links in new tabs
 * 
 * @param htmlContent - HTML content to process
 * @param fontSize - Base font size in pixels
 * @returns Modified HTML content
 */
export function setAttributesForHtml(htmlContent: string, fontSize: number): string {
  // Create a temporary div element to parse the HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlContent;

  // Traverse the DOM to find all <p> tags
  const pTags = tempDiv.querySelectorAll('p');

  // Apply the font size to each <p> tag
  pTags.forEach((pTag) => {
    (pTag as HTMLParagraphElement).style.fontSize = `calc(${fontSize / 16}rem * var(--unicity-font-scale, 1))`;
  });

  // Traverse the DOM to find all <a> tags
  const aTags = tempDiv.querySelectorAll('a');

  // Set the target attribute to "_blank" for each <a> tag to open in a new tab
  aTags.forEach((aTag) => {
    (aTag as HTMLAnchorElement).setAttribute('target', '_blank');
  });

  // Convert the modified DOM back to HTML
  const modifiedHTML = tempDiv.innerHTML;

  return modifiedHTML;
}

